const ACCESS_CHANGED = 'encryption_flow.access_changed'

const EVENT_TITLES = {
  'email.received': 'Ingested',
  'email.action_decided': 'Encrypted',
  'notification.magic_link_generated': 'Magic link generated',
  'email.delivered': 'Sent',
  'email.opened': 'Opened',
  'email.accessed': 'Accessed',
  'email.expired': 'Expired',
  'email.revoked': 'Revoked',
  'email.send_failed': 'Send failed',
}

const ACCESS_ACTION_TITLES = {
  revoked: 'Revoked',
  restored: 'Restored',
}

const INTERACTION_TITLES = {
  view: 'Opened',
  reply: 'Replied',
}

const TIMELINE_INTERACTION_TYPES = new Set(['view', 'reply'])

const humanizeEventType = (eventType = '') =>
  String(eventType)
    .split('.')
    .pop()
    ?.replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Event'

const getEventEmailId = (event) => event?.emailId || event?.email_id || null

const getRawEventType = (event) => event?.eventType || event?.event_type || ''

const getInteractionType = (event) => event?.interactionType || event?.interaction_type || null

export const isSilentModeEvent = (event) =>
  String(getRawEventType(event).split('.').pop() || '').startsWith('silent_mode')

export const hasSilentModeEvent = (events = [], emailId = null) =>
  events.some((event) => {
    if (!isSilentModeEvent(event)) return false
    const eventEmailId = getEventEmailId(event)
    return emailId == null || eventEmailId == null || eventEmailId === emailId
  })

export const isViewInteraction = (event) =>
  (event?.eventType || event?.event_type) === 'email.interaction' &&
  getInteractionType(event) === 'view'

const shouldIncludeRawEvent = (event) => {
  const eventType = event?.eventType || event?.event_type || ''
  if (eventType !== 'email.interaction') return true
  return TIMELINE_INTERACTION_TYPES.has(getInteractionType(event))
}

const buildDescription = (eventType, data = {}, context = {}) => {
  switch (eventType) {
    case 'email.received':
      if (context.sender) return `Received from ${context.sender}`
      if (data.outcomeReason) return data.outcomeReason.replace(/_/g, ' ')
      if (data.recipientCount != null) return `${data.recipientCount} recipients`
      return 'Email received'
    case 'email.action_decided':
      if (context.triggers?.length) return `Policy: ${context.triggers.join(', ')}`
      return 'Encryption action decided'
    case 'notification.magic_link_generated':
      return ''
    case 'email.delivered':
      if (context.recipient) return `Sent to ${context.recipient}`
      if (data.notificationType) return `Notification: ${data.notificationType}`
      return 'Sent to recipient'
    case 'email.interaction':
      if (context.interactionType === 'reply') {
        return context.recipient ? `Replied by ${context.recipient}` : 'Email replied'
      }
      return context.recipient ? `Opened by ${context.recipient}` : 'Email opened'
    case ACCESS_CHANGED:
      return data.actor ? `By ${data.actor}` : ''
    default:
      return data.outcomeReason?.replace(/_/g, ' ') || data.deliveryStatus || ''
  }
}

const deriveEventType = (eventType, data = {}) => {
  if (
    eventType === 'email.send_failed' ||
    data.deliveryStatus === 'failed' ||
    data.outcome === 'failed'
  ) {
    return 'bad'
  }
  if (eventType === 'email.expired' || data.deliveryStatus === 'expired') {
    return 'warn'
  }
  if (eventType === ACCESS_CHANGED && data.action === 'revoked') {
    return 'bad'
  }
  if (data.outcome === 'pending' || data.deliveryStatus === 'pending') {
    return 'muted'
  }
  return 'default'
}

const roundTimestampToSeconds = (ts) => {
  if (!ts) return null
  const ms = new Date(ts).getTime()
  if (Number.isNaN(ms)) return ts
  return new Date(Math.floor(ms / 1000) * 1000).toISOString()
}

const resolveRecipient = (context, emailId, data = {}) => {
  const recipientMeta = context.recipientsByEmailId?.[emailId]
  return (
    (typeof recipientMeta === 'string' ? recipientMeta : recipientMeta?.email) ||
    data.recipient ||
    data.address ||
    data.to ||
    null
  )
}

const isAccessChanged = (event) => getRawEventType(event) === ACCESS_CHANGED

export const latestAccessChange = (events = []) => {
  let latestAction = null
  let latestMs = -Infinity
  events.forEach((event) => {
    if (!isAccessChanged(event)) return
    const ms = new Date(event.eventTimestamp || event.event_timestamp || 0).getTime()
    if (Number.isNaN(ms) || ms < latestMs) return
    latestMs = ms
    latestAction = event.data?.action || null
  })
  if (!latestAction) return null
  return { action: latestAction, at: latestMs }
}

export const filterJourneyEventsByEmailId = (events = [], emailId = null) => {
  if (!emailId) return []
  return events.filter((event) => {
    const eventEmailId = getEventEmailId(event)
    return eventEmailId === emailId || (eventEmailId == null && isAccessChanged(event))
  })
}

/** Keep first view and first reply per recipient emailId. */
export const dedupeFirstInteractions = (events = []) => {
  const seen = new Set()
  return events.filter((event) => {
    if (event.eventType !== 'email.interaction') return true
    const key = `${event.emailId || ''}:${event.interactionType || ''}`
    if (!event.emailId || !event.interactionType || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const mapJourneyEvent = (event = {}, context = {}) => {
  const eventType = event.eventType || event.event_type || ''
  const data = event.data || {}
  const emailId = getEventEmailId(event)
  const interactionType = getInteractionType(event)
  const timestamp = roundTimestampToSeconds(event.eventTimestamp || event.event_timestamp || null)
  const recipient = resolveRecipient(context, emailId, data)
  const sourceId = event.id || event.eventId || event.event_id || null
  const title =
    (eventType === ACCESS_CHANGED && ACCESS_ACTION_TITLES[data.action]) ||
    (eventType === 'email.interaction' && INTERACTION_TITLES[interactionType]) ||
    EVENT_TITLES[eventType] ||
    humanizeEventType(eventType)

  return {
    id: sourceId || `${eventType}-${interactionType || ''}-${emailId || ''}-${timestamp || ''}`,
    title,
    description: buildDescription(eventType, data, {
      ...context,
      recipient,
      interactionType,
    }),
    timestamp,
    type: deriveEventType(eventType, data),
    emailId,
    eventType,
    interactionType,
    userAgent: data.userAgent || data.user_agent || null,
  }
}

/** Map events; keep view + reply only; first of each per recipient. */
export const mapJourneyEvents = (events = [], context = {}) => {
  const mapped = events
    .filter(shouldIncludeRawEvent)
    .map((event) => mapJourneyEvent(event, context))
    .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))

  return dedupeFirstInteractions(mapped)
}

/**
 * Per-recipient view stats from raw API events (all view interactions count).
 */
export const deriveRecipientViewStats = (events = [], emailId = null) => {
  if (!emailId) return { viewCount: 0, openedAt: null, userAgent: null }

  const views = events
    .filter((event) => isViewInteraction(event) && getEventEmailId(event) === emailId)
    .sort(
      (a, b) =>
        new Date(a.eventTimestamp || a.event_timestamp || 0) -
        new Date(b.eventTimestamp || b.event_timestamp || 0)
    )

  const first = views[0]
  const data = first?.data || {}
  return {
    viewCount: views.length,
    openedAt: first ? roundTimestampToSeconds(first.eventTimestamp || first.event_timestamp) : null,
    userAgent: data.userAgent || data.user_agent || null,
  }
}
